import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, Users, ChevronDown, ChevronRight, ArrowLeft, FileText, Database, Activity, ListChecks, Shield, Cloud, Mail } from 'lucide-react';
import axios from 'axios';
import Logo from '@/components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [companySettingsOpen, setCompanySettingsOpen] = useState(false);
  const [userSettingsOpen, setUserSettingsOpen] = useState(false);
  const [newUsersCount, setNewUsersCount] = useState(0);

  useEffect(() => {
    // Check if user is admin - read from sessionStorage first (default login), then localStorage (rememberMe)
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    const userStr = sessionStorage.getItem('user') || localStorage.getItem('user');
    
    if (!token || !userStr) {
      navigate('/');
      return;
    }

    const userData = JSON.parse(userStr);
    if (userData.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    setUser(userData);

    // Load new users count for super admin badge
    if (userData.email === 'admin@specard.net') {
      axios.get(`${BACKEND_URL}/api/admin/new-users/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(res => setNewUsersCount(res.data.count || 0))
        .catch(() => {});
    }

    // Keep company settings open if on company pages
    if (location.pathname.includes('/admin/company-settings')) {
      setCompanySettingsOpen(true);
    }
    
    // Keep user settings open if on user pages
    if (location.pathname.includes('/admin/user-settings')) {
      setUserSettingsOpen(true);
    }
  }, [navigate, location]);

  // Listen for count changes from ListUsers page
  useEffect(() => {
    const handleCountChange = (e) => setNewUsersCount(e.detail.count);
    window.addEventListener('newUsersCountChanged', handleCountChange);
    return () => window.removeEventListener('newUsersCountChanged', handleCountChange);
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-xl">
        <div className="p-6 border-b-4" style={{ borderColor: '#FF6B35' }}>
          <div className="cursor-pointer mb-3" onClick={() => navigate('/dashboard')} data-testid="admin-sidebar-logo">
            <Logo size="md" showText={true} textColor="#1F2937" />
          </div>
          <h2 className="text-lg font-bold text-gray-800 hover:text-orange-600 transition-colors cursor-pointer" onClick={() => navigate('/admin')} data-testid="admin-panel-link">Admin Panel</h2>
          <p className="text-sm text-gray-600 mt-1 select-text" data-testid="admin-email-text">{user.email}</p>
        </div>

        <nav className="p-4">
          {/* Company Settings */}
          <div className="mb-2">
            <button
              onClick={() => setCompanySettingsOpen(!companySettingsOpen)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors group ${
                location.pathname.includes('/admin/company-settings') ? 'bg-orange-50' : 'hover:bg-orange-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <Building2 className={`w-5 h-5 ${location.pathname.includes('/admin/company-settings') ? 'text-orange-600' : 'text-gray-600 group-hover:text-orange-600'}`} />
                <span className={`font-medium whitespace-nowrap ${location.pathname.includes('/admin/company-settings') ? 'text-orange-600' : 'text-gray-700 group-hover:text-orange-600'}`}>
                  Company Settings
                </span>
              </div>
              {companySettingsOpen ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {/* Submenu */}
            {companySettingsOpen && (
              <div className="ml-8 mt-2 space-y-1">
                <button
                  onClick={() => navigate('/admin/company-settings/create')}
                  className={`w-full text-left p-2 pl-4 rounded-lg transition-colors whitespace-nowrap ${
                    isActive('/admin/company-settings/create')
                      ? 'bg-orange-100 text-orange-700 font-medium'
                      : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                  }`}
                >
                  Create Company
                </button>
                <button
                  onClick={() => navigate('/admin/company-settings/list')}
                  className={`w-full text-left p-2 pl-4 rounded-lg transition-colors whitespace-nowrap ${
                    isActive('/admin/company-settings/list')
                      ? 'bg-orange-100 text-orange-700 font-medium'
                      : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                  }`}
                >
                  List Companies
                </button>
              </div>
            )}
          </div>

          {/* User Settings */}
          <div className="mb-2">
            <button
              onClick={() => setUserSettingsOpen(!userSettingsOpen)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors group ${
                location.pathname.includes('/admin/user-settings') ? 'bg-orange-50' : 'hover:bg-orange-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <Users className={`w-5 h-5 ${location.pathname.includes('/admin/user-settings') ? 'text-orange-600' : 'text-gray-600 group-hover:text-orange-600'}`} />
                <span className={`font-medium whitespace-nowrap ${location.pathname.includes('/admin/user-settings') ? 'text-orange-600' : 'text-gray-700 group-hover:text-orange-600'}`}>
                  User Settings
                </span>
              </div>
              {userSettingsOpen ? (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {/* Submenu */}
            {userSettingsOpen && (
              <div className="ml-8 mt-2 space-y-1">
                <button
                  onClick={() => navigate('/admin/user-settings/create')}
                  className={`w-full text-left p-2 pl-4 rounded-lg transition-colors whitespace-nowrap ${
                    isActive('/admin/user-settings/create')
                      ? 'bg-orange-100 text-orange-700 font-medium'
                      : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                  }`}
                >
                  Create User
                </button>
                <button
                  onClick={() => navigate('/admin/user-settings/list')}
                  className={`w-full text-left p-2 pl-4 rounded-lg transition-colors whitespace-nowrap ${
                    isActive('/admin/user-settings/list')
                      ? 'bg-orange-100 text-orange-700 font-medium'
                      : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                  }`}
                >
                  <span className="flex items-center justify-between">
                    List Users
                    {newUsersCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold" data-testid="sidebar-new-users-badge">
                        {newUsersCount}
                      </span>
                    )}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* CSI MasterFormat */}
          <div className="mb-2">
            <button
              onClick={() => navigate('/admin/csi-masterformat')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                isActive('/admin/csi-masterformat') ? 'bg-orange-50' : 'hover:bg-orange-50'
              }`}
            >
              <FileText className={`w-5 h-5 ${isActive('/admin/csi-masterformat') ? 'text-orange-600' : 'text-gray-600 group-hover:text-orange-600'}`} />
              <span className={`font-medium whitespace-nowrap ${isActive('/admin/csi-masterformat') ? 'text-orange-600' : 'text-gray-700 group-hover:text-orange-600'}`}>
                CSI MasterFormat
              </span>
            </button>
          </div>

          {/* Multiple Choice */}
          <div className="mb-2">
            <button
              onClick={() => navigate('/admin/multiple-choice')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                isActive('/admin/multiple-choice') ? 'bg-orange-50' : 'hover:bg-orange-50'
              }`}
            >
              <ListChecks className={`w-5 h-5 ${isActive('/admin/multiple-choice') ? 'text-orange-600' : 'text-gray-600 group-hover:text-orange-600'}`} />
              <span className={`font-medium whitespace-nowrap ${isActive('/admin/multiple-choice') ? 'text-orange-600' : 'text-gray-700 group-hover:text-orange-600'}`}>
                Multiple Choice
              </span>
            </button>
          </div>

          {/* Database Maintenance */}
          <div className="mb-2">
            <button
              onClick={() => navigate('/admin/database-maintenance')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                isActive('/admin/database-maintenance') ? 'bg-orange-50' : 'hover:bg-orange-50'
              }`}
            >
              <Database className={`w-5 h-5 ${isActive('/admin/database-maintenance') ? 'text-orange-600' : 'text-gray-600 group-hover:text-orange-600'}`} />
              <span className={`font-medium whitespace-nowrap ${isActive('/admin/database-maintenance') ? 'text-orange-600' : 'text-gray-700 group-hover:text-orange-600'}`}>
                Database Maintenance
              </span>
            </button>
          </div>

          {/* Activity Log */}
          <div className="mb-2">
            <button
              onClick={() => {
                navigate('/admin/activity-log');
              }}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                isActive('/admin/activity-log') ? 'bg-orange-50' : 'hover:bg-orange-50'
              }`}
            >
              <Activity className={`w-5 h-5 ${isActive('/admin/activity-log') ? 'text-orange-600' : 'text-gray-600 group-hover:text-orange-600'}`} />
              <span className={`font-medium whitespace-nowrap ${isActive('/admin/activity-log') ? 'text-orange-600' : 'text-gray-700 group-hover:text-orange-600'}`}>
                Activity Log
              </span>
            </button>
          </div>

          {/* Auth Activity - Super Admin Only (admin@specard.net) */}
          {user?.email === 'admin@specard.net' && (
            <div className="mb-2">
              <button
                onClick={() => navigate('/admin/auth-activity')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                  isActive('/admin/auth-activity') ? 'bg-orange-50' : 'hover:bg-orange-50'
                }`}
              >
                <Shield className={`w-5 h-5 ${isActive('/admin/auth-activity') ? 'text-orange-600' : 'text-gray-600 group-hover:text-orange-600'}`} />
                <span className={`font-medium whitespace-nowrap ${isActive('/admin/auth-activity') ? 'text-orange-600' : 'text-gray-700 group-hover:text-orange-600'}`}>
                  Auth Activity
                </span>
              </button>
            </div>
          )}

          {/* Cloudinary Dashboard - Super Admin Only */}
          {user?.email === 'admin@specard.net' && (
            <div className="mb-2">
              <button
                onClick={() => navigate('/admin/cloudinary')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                  isActive('/admin/cloudinary') ? 'bg-orange-50' : 'hover:bg-orange-50'
                }`}
                data-testid="sidebar-cloudinary"
              >
                <Cloud className={`w-5 h-5 ${isActive('/admin/cloudinary') ? 'text-orange-600' : 'text-gray-600 group-hover:text-orange-600'}`} />
                <span className={`font-medium whitespace-nowrap ${isActive('/admin/cloudinary') ? 'text-orange-600' : 'text-gray-700 group-hover:text-orange-600'}`}>
                  Cloudinary
                </span>
              </button>
            </div>
          )}

          {/* Mail Ops - Super Admin Only */}
          {user?.email === 'admin@specard.net' && (
            <div className="mb-2">
              <button
                onClick={() => navigate('/admin/mail')}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors group ${
                  isActive('/admin/mail') ? 'bg-orange-50' : 'hover:bg-orange-50'
                }`}
                data-testid="sidebar-mail"
              >
                <Mail className={`w-5 h-5 ${isActive('/admin/mail') ? 'text-orange-600' : 'text-gray-600 group-hover:text-orange-600'}`} />
                <span className={`font-medium whitespace-nowrap ${isActive('/admin/mail') ? 'text-orange-600' : 'text-gray-700 group-hover:text-orange-600'}`}>
                  Mail Ops
                </span>
              </button>
            </div>
          )}

          {/* Back to Main Dashboard */}
          <div className="mt-8 pt-8 border-t">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full p-3 rounded-lg text-white hover:opacity-90 transition-all"
              style={{ background: '#FF6600' }}
            >
              Back to Dashboard
            </button>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
